import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

_pushScopeId("data-v-5c608ecb")
const _hoisted_1 = { id: "upload-art-component" }
const _hoisted_2 = {
  key: 0,
  style: {"width":"100%","display":"flex","justify-content":"center"}
}
_popScopeId()

import { ref, Ref, computed, watch, provide, onMounted } from "vue";
import { store } from "@/store";
import { imgString } from "@/canvas-api/UserObjects/Image";
import useHandleModal from "@/composables/useHandleModal";

import FileTypeModal from "./UploadErrorModals/FileTypeModal.vue";
import FileSizeModal from "./UploadErrorModals/FileSizeModal.vue";
import CropImageModal from "./CropImageModal.vue";
import ToolTitle from "./ToolTitle.vue";
import EditImage from "./EditImage.vue";

/* Type assertion */

export default /*#__PURE__*/_defineComponent({
  setup(__props) {

// Vue 3.2 script setup api
const verifyFile = (file: FileList | null): file is FileList => {
  return file !== null && Object.keys(file).length > 0;
}
const verifyReader = (result: string | ArrayBuffer | null): result is string => {
  return typeof result === "string";
}

/* Mounting */
const title = ref("Upload Art");
const currentActiveObject = computed(() => store.getters.currentActiveObject);

onMounted(() => {
  if (currentActiveObject.value && currentActiveObject.value.name === "image") {
    title.value = "Edit Image";
  }
});

watch(currentActiveObject, (newObject) => {
  if (newObject) {
    title.value = "Edit Image";
  }
});

const { showModal, bounceTransition, fadeTransition, timeOut, handleModal } = useHandleModal();

provide("handleModal", handleModal);
provide("bounceTransition", bounceTransition);
provide("fadeTransition", fadeTransition);

const fileInput = ref<HTMLInputElement>();
const showFileType = ref(false);
const showFileSize = ref(false);
const showCropModal = ref(false);
const currentFile = ref([] as File[]);
const fileSize = ref(0);
const fileName = ref("");

const closeModalAndCleanInput = (): void => {
  if (fileInput.value) fileInput.value.value = "";

  handleModal();

  setTimeout(() => {
    showFileType.value = false;
    showFileSize.value = false;
    showCropModal.value = false;
  }, timeOut.value);
};

const closeErrorModals = (): Promise<void> => {
  return new Promise((resolve) => {
    bounceTransition.value = "transitionUp";

    setTimeout(() => {
      bounceTransition.value = "bounceInDown";
      showFileSize.value = false;
      showFileType.value = false;
      resolve();
    }, timeOut.value);
  });
};

const handleTransition = (closeModal: Ref<boolean>, openModal: Ref<boolean>) => {
  bounceTransition.value = "transitionUp";

  setTimeout(() => {
    bounceTransition.value = "bounceInDown";
    closeModal.value = false;
    openModal.value = true;
  }, timeOut.value);
};

const checkFileSize = (file: File[]): boolean => {
  if (fileSizeInMb(file[0].size) > 4) return true;
  else return false;
};

const fileSizeInMb = (fileSize: number): number => {
  return Number((fileSize / 1024 / 1024).toFixed(2));
};

const checkFileType = (file: File[]): boolean => {
  const fileTypes = ["png", "jpg", "jpeg"];
  const splitFile = file[0].name.split(".");
  const ext = splitFile[splitFile.length - 1].toLowerCase();

  if (!fileTypes.includes(ext)) return true;
  else return false;
};

const continueAnyway = (): void => {
  if (checkFileType(currentFile.value)) {
    fileName.value = currentFile.value[0].name;
    handleTransition(showFileSize, showFileType);
    if (!showModal.value) handleModal();
  } else {
    handleFile(currentFile.value);
  }
};

const handleFile = async (file: File[]): Promise<void> => {
  if (showFileSize.value || showFileType.value) {
    await closeErrorModals();
  }

  const reader = new FileReader();

  reader.onload = async function () {
    const result = reader.result;

    if (verifyReader(result)) {
      imgString.value = result;

      if (!showModal.value) {
        handleModal();
      }

      showCropModal.value = true;
    } else {
      console.log("Did not receive typeof string from reader");
    }

    if (fileInput.value) fileInput.value.value = "";
  };

  reader.readAsDataURL(file[0]);
};

const handleClickAndDrop = (
  event?: { target?: HTMLInputElement },
  fileList?: FileList
): void => {
  let file;
  if (event && event.target) {
    file = event.target.files;
  } else if (fileList) {
    file = fileList;
  } else {
    throw new Error("Did not provide correct type HTMLInputElement or FileList, UploadArt.vue");
  }

  if (verifyFile(file)) {
    currentFile.value = Array.from(file);

    if (checkFileSize(currentFile.value)) {
      fileSize.value = fileSizeInMb(file[0].size);
      handleTransition(showFileType, showFileSize);

      if (!showModal.value) handleModal();
    } else if (checkFileType(currentFile.value)) {
      fileName.value = file[0].name;
      handleTransition(showFileSize, showFileType);

      if (!showModal.value) handleModal();
    } else {
      handleFile(currentFile.value);
    }
  }
};

const reCropImage = () => {
  if (!showModal.value) {
    handleModal();
  }

  showCropModal.value = true;
};

const setFileName = (name: string): void => {
  fileName.value = name;
};

const uploadClick = (): void => {
  if (fileInput.value) {
    fileInput.value.click();
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(ToolTitle, { title: title.value }, null, 8, ["title"]),
    (!_unref(currentActiveObject) || _unref(currentActiveObject).name !== 'image')
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("button", {
            "aria-label": "Upload an image",
            onClick: uploadClick
          }, "Upload an Image"),
          _createElementVNode("input", {
            onChange: handleClickAndDrop,
            ref: (_value, _refs) => {
              _refs['fileInput'] = _value
              fileInput.value = _value
            },
            id: "file-input",
            type: "file",
            name: "user-image",
            hidden: ""
          }, null, 544)
        ]))
      : (_openBlock(), _createBlock(EditImage, {
          key: 1,
          onReCropImage: reCropImage
        })),
    (_unref(showModal) && showFileType.value)
      ? (_openBlock(), _createBlock(FileTypeModal, {
          key: 2,
          onSetFileName: setFileName,
          onUploadClick: uploadClick,
          onHandleClickAndDrop: handleClickAndDrop,
          onCloseModalAndCleanInput: closeModalAndCleanInput,
          "file-name": fileName.value
        }, null, 8, ["file-name"]))
      : _createCommentVNode("", true),
    (_unref(showModal) && showFileSize.value)
      ? (_openBlock(), _createBlock(FileSizeModal, {
          key: 3,
          onContinueAnyway: continueAnyway,
          onUploadClick: uploadClick,
          onCloseModalAndCleanInput: closeModalAndCleanInput,
          "file-size": fileSize.value
        }, null, 8, ["file-size"]))
      : _createCommentVNode("", true),
    (_unref(showModal) && showCropModal.value && _unref(imgString).length)
      ? (_openBlock(), _createBlock(CropImageModal, {
          key: 4,
          onCloseModalAndCleanInput: closeModalAndCleanInput
        }))
      : _createCommentVNode("", true)
  ]))
}
}

})